import React, { useMemo, memo, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useSubserviceWithDetails } from '../hooks/useServiceDetails';
import { useServices, useSubservices } from '../hooks/useAirtable';
import ServiceDetailContent from '../components/ServiceDetailContent';
import ServiceDetailGallery from '../components/ServiceDetailGallery';
import ServiceDetailSidebar from '../components/ServiceDetailSidebar';
import Footer from '../components/Footer';
import Header from '../components/Header';

// Memoized components to prevent unnecessary re-renders
const MemoizedHeader = memo(Header);
const MemoizedFooter = memo(Footer);
const MemoizedSidebar = memo(ServiceDetailSidebar);

const SubServiceDetailPage = ({ onOpenChat, contentOnly = false }) => {
  const { id, lang } = useParams();
  const navigate = useNavigate();
  const { currentLanguage, isRTL, changeLanguage } = useLanguage();
  
  // Determine the language to use - prioritize URL lang over context
  const activeLanguage = (lang && (lang === 'ar' || lang === 'en')) ? lang : currentLanguage;
  const activeIsRTL = activeLanguage === 'ar';

  // عند فتح الصفحة أو تغيير الخدمة: ابدأ من أعلى الصفحة
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [id]);
  
  // Update language from URL if provided, or redirect to language-specific URL
  useEffect(() => {
    if (!id) return;
    
    if (lang && (lang === 'ar' || lang === 'en')) {
      // Language is in URL, update context if different
      if (lang !== currentLanguage) {
        changeLanguage(lang);
      }
    } else {
      // No language in URL, redirect to include language (only once)
      if (currentLanguage) {
        navigate(`/${currentLanguage}/subservice/${id}`, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, id]); // Intentionally limited dependencies to prevent infinite loops

  // Update URL when language changes in context (e.g., from LanguageSwitcher)
  // This handles the case when user clicks language switcher while on subservice page
  useEffect(() => {
    if (!id || !currentLanguage) return;
    
    // Only update if URL lang doesn't match currentLanguage and we have a valid lang in URL
    if (lang && lang !== currentLanguage && (lang === 'ar' || lang === 'en')) {
      navigate(`/${currentLanguage}/subservice/${id}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLanguage]); // Intentionally limited dependencies to prevent infinite loops
  
  // Fetch subservice and its details - use activeLanguage from URL
  const { subservice, details, loading, error } = useSubserviceWithDetails(id, activeLanguage);
  
  // Fetch services and subservices for sidebar and breadcrumbs - use activeLanguage from URL
  const { services } = useServices(true, activeLanguage);
  const { subservices: allSubservices } = useSubservices(null, true, activeLanguage);

  // Memoize arrays to prevent reference changes
  const memoizedServices = useMemo(() => services || [], [services]);
  const memoizedSubservices = useMemo(() => allSubservices || [], [allSubservices]);

  // Memoize parent service to prevent recalculation
  const parentService = useMemo(() => {
    return subservice?.parent_service_id 
      ? services?.find(s => String(s.id) === String(subservice.parent_service_id))
      : null;
  }, [subservice?.parent_service_id, services]);

  // Memoize description to prevent recalculation
  // Use Arabic description when language is Arabic, fallback to English if Arabic is empty
  const description = useMemo(() => {
    if (details) {
      if (activeLanguage === 'ar') {
        // Always use description_ar when language is Arabic, fallback to description_en only if description_ar is empty
        return details.description_ar || details.description_en || '';
      } else {
        return details.description_en || details.description_ar || '';
      }
    }
    // Fallback to subservice description - use description_ar directly when Arabic
    if (activeLanguage === 'ar') {
      return subservice?.description_ar || subservice?.description_en || '';
    } else {
      return subservice?.description_en || subservice?.description_ar || '';
    }
  }, [details, activeLanguage, subservice?.description_ar, subservice?.description_en]);

  const skeletonShimmer = 'linear-gradient(90deg, #e2e8f0 0%, #f1f5f9 50%, #e2e8f0 100%)';
  const skeletonStyle = (delay = 0) => ({
    background: skeletonShimmer,
    backgroundSize: '200% 100%',
    animation: 'skeleton-shimmer 1.5s ease-in-out infinite',
    animationDelay: `${delay}s`,
    borderRadius: '8px'
  });

  if (loading) {
    return (
      <div style={{
        minHeight: contentOnly ? 'auto' : '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#f9fafb'
      }} dir={activeIsRTL ? 'rtl' : 'ltr'}>
        {!contentOnly && <MemoizedHeader onOpenChat={onOpenChat} />}
        {!contentOnly && <div style={{ height: '100px', width: '100%' }} />}
        <div style={{
          display: 'flex',
          width: '100%',
          minHeight: contentOnly ? 'auto' : 'calc(100vh - 180px)'
        }}>
          {!contentOnly && (
            <aside style={{
              width: '20%',
              minWidth: '200px',
              flexShrink: 0,
              padding: '20px',
              borderRight: activeIsRTL ? 'none' : '1px solid #e2e8f0',
              borderLeft: activeIsRTL ? '1px solid #e2e8f0' : 'none'
            }}>
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} style={{ height: '36px', marginBottom: '12px', ...skeletonStyle(i * 0.08) }} />
              ))}
            </aside>
          )}
          <main style={{
            flex: 1,
            minWidth: 0,
            padding: '40px',
            paddingLeft: activeIsRTL ? '40px' : '60px',
            paddingRight: activeIsRTL ? '60px' : '40px'
          }}>
            {/* Breadcrumbs */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
              <div style={{ height: '14px', width: '52px', ...skeletonStyle(0) }} />
              <div style={{ height: '10px', width: '8px', backgroundColor: '#cbd5e1', borderRadius: '2px' }} />
              <div style={{ height: '14px', width: '64px', ...skeletonStyle(0.05) }} />
              <div style={{ height: '10px', width: '8px', backgroundColor: '#cbd5e1', borderRadius: '2px' }} />
              <div style={{ height: '14px', width: '72px', ...skeletonStyle(0.1) }} />
              <div style={{ height: '10px', width: '8px', backgroundColor: '#cbd5e1', borderRadius: '2px' }} />
              <div style={{ height: '14px', width: '40%', maxWidth: '200px', ...skeletonStyle(0.15) }} />
            </div>
            {/* Image placeholder */}
            <div style={{
              width: '100%',
              aspectRatio: '16/9',
              maxHeight: '400px',
              borderRadius: '12px',
              marginBottom: '32px',
              overflow: 'hidden',
              ...skeletonStyle(0.2)
            }} />
            {/* Title */}
            <div style={{ height: '40px', width: '65%', maxWidth: '480px', marginBottom: '16px', ...skeletonStyle(0.25) }} />
            {/* Description lines */}
            <div style={{ marginBottom: '32px' }}>
              <div style={{ height: '18px', width: '100%', marginBottom: '12px', ...skeletonStyle(0.3) }} />
              <div style={{ height: '18px', width: '92%', marginBottom: '12px', ...skeletonStyle(0.35) }} />
              <div style={{ height: '18px', width: '78%', ...skeletonStyle(0.4) }} />
            </div>
            {/* Content cards */}
            {[1, 2, 3].map((i) => (
              <div key={i} style={{
                marginBottom: '24px',
                padding: '24px',
                background: 'white',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
              }}>
                <div style={{ height: '22px', width: '180px', marginBottom: '16px', ...skeletonStyle(0.45 + i * 0.1) }} />
                <div style={{ height: '14px', width: '100%', marginBottom: '10px', ...skeletonStyle(0.5 + i * 0.1) }} />
                <div style={{ height: '14px', width: '95%', marginBottom: '10px', ...skeletonStyle(0.55 + i * 0.1) }} />
                <div style={{ height: '14px', width: '70%', ...skeletonStyle(0.6 + i * 0.1) }} />
              </div>
            ))}
          </main>
        </div>
        {!contentOnly && <MemoizedFooter />}
        <style>{`
          @keyframes skeleton-shimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        {!contentOnly && <MemoizedHeader onOpenChat={onOpenChat} />}
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-800 mb-4">Error Loading Service</h1>
            <p className="text-gray-600 mb-4">{error.message || 'Failed to load service details'}</p>
            <Link to="/" className="text-blue-600 hover:underline">Return to Home</Link>
          </div>
        </div>
      </div>
    );
  }

  if (!subservice) {
    return (
      <div className="min-h-screen bg-gray-50">
        {!contentOnly && <MemoizedHeader onOpenChat={onOpenChat} />}
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-800 mb-4">Service Not Found</h1>
            <p className="text-gray-600 mb-4">The requested service could not be found.</p>
            <Link to="/" className="text-blue-600 hover:underline">Return to Home</Link>
          </div>
        </div>
      </div>
    );
  }

  const subserviceImage = details?.image || subservice?.image;
  const subserviceTitle = activeLanguage === 'ar'
    ? (subservice?.title_ar || subservice?.title || subservice?.name || 'Service Details')
    : (subservice?.title || subservice?.title_en || subservice?.name || 'Service Details');

  return (
    <div style={{
      minHeight: contentOnly ? 'auto' : '100vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#f9fafb'
    }} dir={activeIsRTL ? 'rtl' : 'ltr'}>
      {!contentOnly && <MemoizedHeader onOpenChat={onOpenChat} />}
      {!contentOnly && <div style={{ height: '100px', width: '100%' }} />}
      <div style={{
        display: 'flex',
        width: '100%',
        position: 'relative',
        minHeight: contentOnly ? 'auto' : 'calc(100vh - 180px)',
        marginBottom: '0'
      }}>
        {!contentOnly && (
        <aside className="hidden lg:block" style={{
          width: '20%',
          flexShrink: 0,
          position: 'sticky',
          top: '100px',
          alignSelf: 'flex-start',
          height: 'calc(100vh - 100px)',
          maxHeight: 'calc(100vh - 100px)',
          overflowY: 'auto',
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          padding: '20px',
          paddingLeft: activeIsRTL ? '20px' : '0',
          paddingRight: activeIsRTL ? '0' : '20px',
          backgroundColor: '#f9fafb',
          borderRight: activeIsRTL ? 'none' : '1px solid #e2e8f0',
          borderLeft: activeIsRTL ? '1px solid #e2e8f0' : 'none'
        }}>
          <MemoizedSidebar
            services={memoizedServices}
            currentService={parentService}
            currentSubservice={subservice}
            subservices={memoizedSubservices}
            language={activeLanguage}
            onServiceClick={(serviceId) => navigate(`/${activeLanguage}/service/${serviceId}`, { replace: false })}
            onSubserviceClick={(subserviceId) => navigate(`/${activeLanguage}/subservice/${subserviceId}`, { replace: false })}
            onOpenChat={onOpenChat}
          />
        </aside>
        )}

        <main style={{
          flex: 1,
          minWidth: 0,
          maxWidth: '100%',
          padding: '40px',
          paddingLeft: activeIsRTL ? '40px' : '60px',
          paddingRight: activeIsRTL ? '60px' : '40px',
          paddingBottom: '0',
          overflowY: 'auto',
          position: 'relative'
        }}>
          {/* Breadcrumbs - نفس تنسيق صفحة الخدمات */}
          <nav className="service-breadcrumb" style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '20px',
            flexWrap: 'wrap',
            gap: '8px'
          }}>
            <Link
              to="/"
              style={{
                color: '#64748b',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'color 0.2s'
              }}
              onMouseEnter={(e) => { e.target.style.color = '#1a1a2e'; }}
              onMouseLeave={(e) => { e.target.style.color = '#64748b'; }}
            >
              {activeLanguage === 'ar' ? 'الرئيسية' : 'Home'}
            </Link>
            <span className="service-breadcrumb-sep" style={{ color: '#94a3b8' }} aria-hidden="true">/</span>
            <Link
              to="/#services"
              style={{
                color: '#64748b',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'color 0.2s'
              }}
              onMouseEnter={(e) => { e.target.style.color = '#1a1a2e'; }}
              onMouseLeave={(e) => { e.target.style.color = '#64748b'; }}
            >
              {activeLanguage === 'ar' ? 'الخدمات' : 'Services'}
            </Link>
            {parentService && (
              <>
                <span className="service-breadcrumb-sep" style={{ color: '#94a3b8' }} aria-hidden="true">/</span>
                <Link
                  to={`/${activeLanguage}/service/${parentService.id}`}
                  style={{
                    color: '#64748b',
                    textDecoration: 'none',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'color 0.2s'
                  }}
                  onMouseEnter={(e) => { e.target.style.color = '#1a1a2e'; }}
                  onMouseLeave={(e) => { e.target.style.color = '#64748b'; }}
                >
                  {activeLanguage === 'ar' ? (parentService.title_ar || parentService.title || parentService.name) : (parentService.title || parentService.title_en || parentService.name)}
                </Link>
              </>
            )}
            <span className="service-breadcrumb-sep" style={{ color: '#94a3b8' }} aria-hidden="true">/</span>
            <span className="service-breadcrumb-current" style={{ color: '#1a1a2e', fontSize: '14px', fontWeight: '500' }}>
              {subserviceTitle}
            </span>
          </nav>

          {/* صورة الخدمة الفرعية مع العنوان - نفس تنسيق صفحة الخدمات */}
          {subserviceImage && (
            <div style={{
              width: '100%',
              aspectRatio: '16/9',
              maxHeight: '600px',
              borderRadius: '12px',
              overflow: 'hidden',
              marginBottom: '40px',
              position: 'relative',
              display: 'flex',
              alignItems: 'flex-end',
              backgroundColor: '#f1f5f9'
            }}>
              <img
                src={subserviceImage}
                alt={subserviceTitle}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center center',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0
                }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <div style={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(to top, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.4) 50%, rgba(0, 0, 0, 0) 100%)',
                pointerEvents: 'none'
              }} />
              <div style={{
                position: 'relative',
                zIndex: 2,
                width: '100%',
                padding: '40px',
                paddingBottom: '50px'
              }}>
                <h1 style={{
                  fontSize: 'clamp(36px, 5vw, 56px)',
                  fontWeight: '700',
                  lineHeight: '1.2',
                  margin: 0,
                  color: '#ffffff',
                  letterSpacing: '-0.02em',
                  textAlign: activeIsRTL ? 'right' : 'left',
                  textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
                }}>
                  {subserviceTitle}
                </h1>
              </div>
            </div>
          )}

          {/* عنوان فقط عند عدم وجود صورة - نفس تنسيق صفحة الخدمات */}
          {!subserviceImage && (
            <h1 style={{
              fontSize: 'clamp(32px, 5vw, 48px)',
              fontWeight: '700',
              lineHeight: '1.2',
              marginBottom: '30px',
              color: '#1a1a2e',
              letterSpacing: '-0.02em',
              textAlign: activeIsRTL ? 'right' : 'left'
            }}>
              {subserviceTitle}
            </h1>
          )}

          {/* المحتوى */}
          <ServiceDetailContent
            subservice={subservice}
            details={details}
            language={activeLanguage}
            onOpenChat={onOpenChat}
          />

          {/* المعرض */}
          {details?.gallery && details.gallery.length > 0 && (
            <div style={{ marginTop: '40px' }}>
              <ServiceDetailGallery
                images={details.gallery}
                title={details.title}
              />
            </div>
          )}
        </main>
      </div>

      {!contentOnly && (
      <>
        <div style={{ width: '100%', marginTop: '60px', paddingTop: '0' }}>
          <MemoizedFooter />
        </div>
        <div className="lg:hidden block" style={{ maxWidth: '1400px', margin: '0 auto', padding: '20px' }}>
          <div style={{ width: '100%', marginBottom: '24px' }}>
            <MemoizedSidebar
              services={memoizedServices}
              currentService={parentService}
              currentSubservice={subservice}
              subservices={memoizedSubservices}
              language={activeLanguage}
              onServiceClick={(sid) => navigate(`/${activeLanguage}/service/${sid}`, { replace: false })}
              onSubserviceClick={(subId) => navigate(`/${activeLanguage}/subservice/${subId}`, { replace: false })}
              onOpenChat={onOpenChat}
            />
          </div>
          <div style={{ marginTop: '60px', paddingTop: '40px' }}>
            <MemoizedFooter />
          </div>
        </div>
      </>
      )}

      <style>{`
        /* محاذاة مسار التنقل: النص والفواصل في مستوى واحد */
        .service-breadcrumb { align-items: center !important; }
        .service-breadcrumb > a,
        .service-breadcrumb > span {
          display: inline-flex !important;
          align-items: center !important;
          line-height: 1.2 !important;
        }
        .service-breadcrumb-sep {
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          line-height: 1 !important;
          font-size: inherit !important;
        }
        @media (max-width: 1024px) {
          aside.hidden.lg\\:block { display: none !important; }
          div.lg\\:hidden.block { display: block !important; }
          main { padding: 20px !important; padding-left: 20px !important; padding-right: 20px !important; }
        }
        @media (min-width: 1025px) {
          div.lg\\:hidden.block { display: none !important; }
        }
        @media (max-width: 768px) {
          main {
            padding: 18px 16px 24px !important;
            padding-left: 16px !important;
            padding-right: 16px !important;
          }
          .service-breadcrumb {
            display: flex !important;
            flex-wrap: wrap !important;
            align-items: center !important;
            gap: 4px 6px !important;
            margin-bottom: 14px !important;
            line-height: 1.4 !important;
          }
          .service-breadcrumb a,
          .service-breadcrumb span:not(.service-breadcrumb-current) {
            font-size: 12px !important;
          }
          .service-breadcrumb-sep { font-size: 12px !important; }
          .service-breadcrumb-current {
            font-size: 12px !important;
            max-width: 100% !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
            white-space: nowrap !important;
          }
          main nav {
            margin-bottom: 14px !important;
          }
          main nav a, main nav span {
            font-size: 13px !important;
          }
          main > div[style*="aspectRatio"] {
            max-height: 280px !important;
            margin-bottom: 24px !important;
          }
          main > div[style*="aspectRatio"] > div:last-of-type {
            padding: 24px 16px 28px !important;
          }
          main > h1, h1[style*="fontSize"] {
            font-size: 24px !important;
            margin-bottom: 16px !important;
          }
          .rich-text-content {
            font-size: 15px !important;
            line-height: 1.7 !important;
          }
        }
        @media (max-width: 480px) {
          main {
            padding: 14px 12px 20px !important;
            padding-left: 12px !important;
            padding-right: 12px !important;
          }
          .service-breadcrumb { gap: 3px 5px !important; margin-bottom: 12px !important; }
          .service-breadcrumb a,
          .service-breadcrumb span:not(.service-breadcrumb-current) { font-size: 11px !important; }
          .service-breadcrumb-sep { font-size: 11px !important; }
          .service-breadcrumb-current { font-size: 11px !important; }
          main nav {
            margin-bottom: 12px !important;
          }
          main nav a, main nav span {
            font-size: 12px !important;
          }
          main > div[style*="aspectRatio"] {
            max-height: 220px !important;
            margin-bottom: 20px !important;
          }
          main > div[style*="aspectRatio"] > div:last-of-type {
            padding: 18px 12px 22px !important;
          }
          main > h1, h1[style*="fontSize"] {
            font-size: 20px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default SubServiceDetailPage;
